import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
    LeftArrowIcon,
    HeartIcon,
    LinkIcon,
    PrintIcon,
    DownloadIcon,
    FavoritesIcon,
    PlayIcon,
    VoiceSquare,
    AudioPlayer,
} from '../components';
import { Skeleton } from "../components/ui/skeleton";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
} from "src/components/ui/tabs"

import { useLocation, useParams } from 'react-router-dom';
import { fetchSinglePrayerRite, toggleFavorite } from "../utilities/api";
import { mapSinglePrayerData } from '../utilities/dataTransform'
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import TrinityAudioPlayer from "./TrinityAudioPlayer";
import { useReactToPrint } from "react-to-print";

// Utility function to decode HTML entities
const decodeHtml = (html) => {
    if (!html) return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

// Static fallback data
const STATIC_FALLBACK = {
    title: 'Rem magnam sapiente fugit provident',
    slug: 'rem-magnam-sapiente-fugit-provident',
    excerpt: 'Delectus recusandae est et provident quia ea accusamus.',
    content: 'Culture & Customs Exploration',
    status: 'publish',
    created_at: '2025-02-20T01:54:59Z',
    updated_at: '2025-02-20T01:54:59Z',
    imageUrl: `${process.env.PUBLIC_URL}/media/images/group-of-catechesis-title.jpeg`,
    author: 'John Doe',
    source: 'Name of the book',
    categories: ['Sacred Art', 'Easter'],
    favorite: false,
    duration: '10 minutes',
    description: 'Sacred art has always held a profound significance in human culture.',
    full_description: 'Sacred art has always held a profound significance in human culture, serving as a visual representation of spirituality, faith, and devotion.'
};

const TabsData = ({ english, latin }) => [
    {
        value: "english",
        label: "English",
        content: english || (
            <>
                <h2 className="py-6 text-lg font-bold text-gray-900">Title</h2>
                <div>
                    <h4 className="pb-2 text-base font-bold text-gray-900">Introduction</h4>
                    <p className="pb-6 text-lg text-gray-700">Sacred art has always held a profound significance in human culture, serving as a visual representation of spirituality, faith, and devotion. Across different traditions and beliefs, artists have expressed their deepest emotions and beliefs through paintings, sculptures, architecture, and various forms of artistic expression. In this post, we delve into the captivating world of sacred art, exploring its beauty, symbolism, and the messages it conveys.</p>
                </div>
                <div>
                    <h4 className="pb-2 text-base font-bold text-gray-900">The Beauty of Sacred Art</h4>
                    <p className="pb-6 text-lg text-gray-700">One of the most striking aspects of sacred art is its inherent beauty. Whether it's the intricate details of a religious painting, the awe-inspiring architecture of a cathedral, or the serene sculptures depicting deities, sacred art captivates the viewer with its aesthetic appeal. Artists often use vibrant colors, harmonious compositions, and exquisite craftsmanship to create pieces that evoke a sense of wonder and reverence.</p>
                </div>
                <div>
                    <h4 className="pb-2 text-base font-bold text-gray-900">Symbolism and Meaning</h4>
                    <p className="pb-6 text-lg text-gray-700">Beyond its visual allure, sacred art is laden with symbolism and deep meaning. Every element within a piece of sacred art carries significance, conveying spiritual truths, stories from religious texts, or representing divine beings and concepts. For example, in Christian art, the use of light symbolizes divine presence, while certain animals like the dove symbolize peace and purity. Understanding these symbols enhances our appreciation of the art and its connection to faith and spirituality.</p>
                </div>
                <div>
                    <h4 className="pb-2 text-base font-bold text-gray-900">Cultural and Historical Significance</h4>
                    <p className="pb-6 text-lg text-gray-700">Sacred art also holds immense cultural and historical significance. It reflects the beliefs, values, and practices of different cultures and civilizations throughout history. From ancient temples adorned with intricate carvings to Renaissance masterpieces depicting biblical scenes, sacred art provides a window into the past and offers insights into how people perceived and worshiped the divine.</p>
                </div>
            </>
        ),
    },
    {
        value: "latin",
        label: "Latin",
        content: latin || (
            <p>
                Every Sunday, the church bells rang out across the valley, calling the faithful to worship. Among them was an old man named Thomas, who had attended the church since he was a boy. His weathered hands clasped a worn Bible, its pages filled with his annotations and prayers.
            </p>
        ),
    },
];

const SinglePrayerAndRites = () => {
    const { slug } = useParams();
    const { copied, copyToClipboard } = useCopyToClipboard();
    const [activeTab, setActiveTab] = useState('english');
    const [isChecked, setIsChecked] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [activePlayerIndex, setActivePlayerIndex] = useState(null); // Track active audio player
    const audioRef = useRef(null);
    const printRef = useRef(null);
    const printTopRef = useRef(null);
    const printMidRef = useRef(null);
    const printBottomRef = useRef(null);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            setData(null);

            try {
                setData(null); // Reset state before fetching
                const result = await fetchSinglePrayerRite(slug);
                if (result && result.data) {
                    const transformedData = mapSinglePrayerData(result.data, result.included);
                    setData(transformedData);
                } else {
                    setData(STATIC_FALLBACK);
                    setError("No data found, using default content");
                }
            } catch (err) {
                console.error("Fetch Error:", err);
                setError(`Failed to fetch data: ${err.message}`);
                setData(STATIC_FALLBACK);
            } finally {
                setLoading(false);
            }
        };

        if (slug) fetchData();
        else {
            setData(STATIC_FALLBACK);
            setLoading(false);
        }

        return () => controller.abort();
    }, [slug]);

    useEffect(() => {
        console.log("Component Re-render Favorite:", data?.favorite);
    }, [data]);


    //  Optimized Favorite Toggle Function
    const handleToggleFavorite = async (event, id) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const currentFavorite = data.favorite

        if (!data || !id) return;

        try {
            const updatedFavoriteStatus = !data.favorite;
            const catechesisId = String(data.id);

            // Step 1: Optimistically update UI
            setData((prevData) => ({
                ...prevData,
                favorite: updatedFavoriteStatus,
            }));

            // Step 3: Call API (Do NOT reload everything)
            await toggleFavorite('prayer-rites', catechesisId, false, currentFavorite);
        } catch (error) {
            console.error("Failed to update favorite:", error);

            // Step 4: If API call fails, revert UI
            setData((prevData) => ({
                ...prevData,
                favorite: !prevData.favorite,
            }));
        }
    };
    // Pass extracted Latin content to TabsData
    const tabsDataWithContent = TabsData({
        english: data?.content ? <div dangerouslySetInnerHTML={{ __html: data.content }} /> : null,
        latin: data?.latin_content ? <div dangerouslySetInnerHTML={{ __html: data.latin_content }} /> : null
    });

    const audioMap = {
        english: data?.english_audio || "",
        latin: data?.latin_audio || "",
    };

    // const handlePlayClick = (index) => {
    //     if (activePlayerIndex === index) {
    //         setActivePlayerIndex(null);
    //         if (audioRef.current) audioRef.current.pause();
    //     } else {
    //         setActivePlayerIndex(index);
    //         setTimeout(() => {
    //             if (audioRef.current) audioRef.current.play();
    //         }, 0);
    //     }
    // };

    // const handlePlayClick = (index) => {
    //     const lang = index === 0 ? 'english' : 'latin';
    //     setActiveTab(lang); // Switch tab first

    //     // Wait for content to render in new tab before playing
    //     setTimeout(() => {
    //         const audioUrl = audioData[index]?.src;
    //         const isValidUrl = typeof audioUrl === 'string' && audioUrl.match(/\.(mp3|ogg|wav)$/i);

    //         if (isValidUrl) {
    //             setActivePlayerIndex(index); // Show native player
    //             setTimeout(() => {
    //                 if (audioRef.current) audioRef.current.play();
    //             }, 0);
    //         } else {
    //             setActivePlayerIndex(null); // Hide native player
    //             // Trinity will auto-inject via useEffect in TabsContent
    //         }
    //     }, 200); // Allow tab content to render first
    // };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: data?.title || "Prayer&Rites",
        onAfterPrint: () => console.log("Printing complete")
    })
    const handleNativePrint = () => {
        const topEl = printTopRef.current;
        const midEl = printMidRef.current;
        const bottomEl = printBottomRef.current;

        if (!topEl || !bottomEl || !midEl) {
            console.error("Print sections are not ready");
            return;
        }

        const printWindow = window.open('', '', 'width=800,height=600');
        if (!printWindow) return;

        // const htmlContent = printRef.current.innerHTML;
        // const clonedNode = printRef.current.cloneNode(true);

        const styles = `
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
            <style>
                * { box-sizing: border-box; }
                img { max-width: 100%; max-height:100vh; object-fit:contain; height:auto; }
                @media print {
                body {
                    background: white !important;
                    color: black !important;
                    font-family: 'Red Hat Display', sans-serif !important;
                }
                }
                 .print-hidden {
            display: none !important;
        }
            .print-page-break {
  page-break-before: always;
}

                 
            [data-state="active"] {
        color: #B6862C !important;         /* text-gold-500 */
        border-color: #B6862C !important;  /* border-gold-500 */
        font-weight: 600 !important;       /* font-semibold */
      }
         .bg-gold-300 {
            background-color: #E5B94F !important;
        }

        .bg-gold-200 {
            background-color: #FCEFCA !important;
        }

        .text-gray-0 {
            color: white !important;
        }
            </style>
            `;

        const topClone = topEl.cloneNode(true);
        const midClone = midEl.cloneNode(true);
        const bottomClone = bottomEl.cloneNode(true);


        printWindow.document.open();
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                <title>${data?.title || 'Print Preview'}</title>
                ${styles}
                </head>
                <body></body>
            </html>
            `);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.document.body.appendChild(topClone);
            printWindow.document.body.appendChild(midClone);
            printWindow.document.body.appendChild(bottomClone);
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            }, 300);
        };
    };



    const handlePlayClick = (index) => {
        const lang = index === 0 ? 'english' : 'latin';
        const audioUrl = audioData[index]?.src;
        const isValidUrl = typeof audioUrl === 'string' && audioUrl.match(/\.(mp3|ogg|wav)$/i);

        // If same tab is active
        if (activeTab === lang) {
            if (activePlayerIndex === index) {
                // Toggle: If already playing this, pause or remove Trinity
                setActivePlayerIndex(null);
                if (audioRef.current) {
                    audioRef.current.pause();
                }

                // Remove Trinity iframe if present
                document.querySelectorAll('iframe').forEach((iframe) => {
                    if (iframe.src.includes('trinitymedia.ai')) {
                        iframe.remove();
                        console.log('[Trinity] Removed iframe on second click');
                    }
                });
                const oldScript = document.getElementById('trinity-script');
                if (oldScript) {
                    oldScript.remove();
                    console.log('[Trinity] Removed script on second click');
                }

                return; // Exit early
            }
            setActiveTab(lang);
        }

        // Set tab and start playing
        setActiveTab(lang);

        setTimeout(() => {
            if (isValidUrl) {
                setActivePlayerIndex(index);
                setTimeout(() => {
                    if (audioRef.current) audioRef.current.play();
                }, 0);
            } else {
                setActivePlayerIndex(null); // ensure native is hidden
            }
        }, 200);
    };


    const handleCloseAudioPlayer = () => {
        setActivePlayerIndex(null);
        if (audioRef.current) audioRef.current.pause();
    };
    const handleProgressUpdate = (progress) => {
        setProgressPercentage(progress);
    };
    // const isValidAudioUrl = (url) =>
    //     typeof url === "string" && /\.(mp3|ogg|wav)$/i.test(url);
    // console.log("Audio src check:",  typeof  isValidAudioUrl());

    const audioData = [
        {
            title: "Play English",
            src: data?.english_audio || "",
            excerpt: "English Language Audio",
        },
        {
            title: "Play Latin",
            src: data?.latin_audio || "",
            excerpt: "Latin Language Audio",
        },
    ];
    return (
        <div className='w-full h-auto'>
            {error && (
                <div className="px-4 py-2 mb-4 text-yellow-800 bg-yellow-100 rounded-md">
                    {error}
                </div>
            )}
            <div>
                <div className="h-full"
                >
                    <div className='flex flex-col w-full h-full gap-8 lg:flex-row md:gap-0'>
                        <div className="w-full lg:max-w-[40%] max-w-full relative border-gray-200 lg:border-r">
                            <div className='px-4 border-b border-gray-100 xl:px-12'>
                                <div className="flex justify-between pt-8 pb-4 md:pb-8 print:hidden">
                                    <div>
                                        <Link className='flex items-center gap-3 group print:hidden' to='/prayer-and-rites'>
                                            <div className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11">
                                                <LeftArrowIcon className="text-gray-700 group-hover:text-gold-500" />
                                            </div>
                                            <h5 className="font-normal text-base leading-5.5 text-gray-600 group-hover:text-gold-500">
                                                Back to Prayers & Rites
                                            </h5>
                                        </Link>
                                    </div>
                                    <div className="flex gap-2 lg:hidden print:hidden">
                                        {
                                            loading || !data ? (
                                                <Skeleton className="rounded-full h-7 w-7" />

                                            ) : (
                                                <button
                                                    key={data.favorite} //  Forces re-render when favorite state changes
                                                    type="button"
                                                    onClick={(e) => handleToggleFavorite(e, data.id)}
                                                    className="focus:outline-none"
                                                >
                                                    <FavoritesIcon
                                                        filled={Boolean(data?.favorite)}
                                                        className={`w-7 h-7 transition-colors duration-200 ${data?.favorite ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                                                            }`}
                                                    />
                                                </button>
                                            )
                                        }
                                        <a href="#" className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group">
                                            <DownloadIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
                                        </a>
                                        <button type="button" onClick={(e) => {
                                            e.preventDefault();
                                            handleNativePrint()
                                        }} className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group">
                                            <PrintIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
                                        </button>
                                        <a
                                            href="#"
                                            className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
                                            onClick={(e) => {
                                                e.preventDefault(); // Prevent navigation
                                                copyToClipboard();
                                            }}
                                        >
                                            <LinkIcon className={`text-gray-700 w-7 h-7 group-hover:text-gold-500 ${copied ? "text-green-500 group-hover:text-green-500" : ""}`} />
                                        </a>
                                    </div>
                                </div>

                                <div className='h-[170px]' ref={printTopRef}>
                                    {loading ? (
                                        //  Skeleton for Image while loading
                                        <Skeleton className="w-full h-full rounded-xl" />
                                    ) : (
                                        <img
                                            src={data?.imageUrl}
                                            alt={data?.title}
                                            className="object-cover w-full h-full rounded-xl print:rounded-none print:max-w-full print:h-auto"
                                            onError={(e) => {
                                                e.target.src = STATIC_FALLBACK.imageUrl;
                                            }}
                                        />
                                    )}
                                </div>

                                <div className='flex flex-col mt-8 mb-8'>
                                    <h4 className='font-bold text-2xl leading-7.5 text-gray-900' ref={printMidRef}> {loading ? <Skeleton className="w-3/4 h-8 rounded" /> : data?.title}
                                    </h4>
                                    <div className='flex flex-row flex-wrap gap-2 mt-3'>
                                        {
                                            loading ? (
                                                <Skeleton className="w-1/3 h-4 rounded" />
                                            ) : (

                                                data?.categories && data.categories.map((category, index) => (
                                                    <span key={index} className='px-2 py-1 text-xs bg-gold-50 text-gold-500 rounded-xl'>{category}</span>
                                                ))
                                            )
                                        }
                                        {/* CatList (Seasons and Nationalities) */}
                                        {data?.catList && data.catList.map((cat, index) => (
                                            <span key={`cat-${index}`} className='px-2 py-1 text-xs font-medium leading-4 bg-gold-50 text-gold-500 rounded-27'>
                                                {cat}
                                            </span>
                                        ))}

                                    </div>
                                    <div className="my-8">
                                        {audioData.map((audio, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handlePlayClick(index)}
                                                className="cursor-pointer w-full max-w-[320px] bg-gold-300 rounded-[50px] p-2 flex items-center justify-between my-2"
                                            >
                                                <div className="flex items-center justify-center rounded-full bg-gold-200 md:h-11 md:w-11 h-9 w-9">
                                                    {activePlayerIndex === index ? (
                                                        <VoiceSquare progress={progressPercentage / 100} />
                                                    ) : (
                                                        <PlayIcon className="text-gray-0" />
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-center flex-grow select-none">
                                                    <p className="text-lg font-bold text-gray-0 print:hidden">{audio.title}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>


                                </div>
                                <div className='fixed bottom-0 xl:left-[16.66%] md:left-[15rem] left-0 right-0 z-50 print:hidden'>
                                    {activePlayerIndex !== null && (
                                        <div className="bg-white-50 shadow-xl animate-slideUp">
                                            <AudioPlayer
                                                src={audioData[activePlayerIndex]?.src}
                                                title={audioData[activePlayerIndex].title}
                                                excerpt={audioData[activePlayerIndex].excerpt}
                                                onClose={handleCloseAudioPlayer}
                                                audioRef={audioRef}
                                                onProgressUpdate={handleProgressUpdate}
                                                isPlaying={activePlayerIndex !== null}
                                                setIsPlaying={(val) => {
                                                    if (!val) setActivePlayerIndex(null);
                                                }} />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='hidden px-4 mt-8 mb-8 xl:px-12 lg:mb-0 lg:block'>
                                <h5 className='mb-3 text-base font-semibold leading-5 text-gray-900'>Author:</h5>
                                <div className='flex flex-row items-center gap-2 mb-8'>
                                    {loading ? (
                                        <Skeleton className="w-20 h-5 rounded" />
                                    ) : (
                                        <p className='font-medium text-sm leading-4.5 text-gray-700'>{data?.author.name}</p>
                                    )}

                                    {/* <img src={`${process.env.PUBLIC_URL}/favicon.png`} alt="Favicon" className="w-7 h-7" />
                                    <p className='font-medium text-sm leading-4.5 text-gray-700'>{data?.relationships?.author}</p> */}
                                </div>
                                <h5 className='mb-3 text-base font-semibold leading-5 text-gray-900'>Source:</h5>
                                <p className='p-4 text-base font-semibold leading-5 text-gray-900 bg-gold-50 rounded-10'>
                                    {loading ? (<Skeleton className="w-1/3 h-4 rounded" />) : (<> This appears in <span className="text-gold-300">
                                        "{data?.source || 'Name of the book'}"
                                    </span>
                                    </>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className='w-full px-4 mt-8 xl:px-12 lg:mt-0' ref={printBottomRef}>
                            <div className='justify-end hidden gap-2 pt-8 pb-8 lg:flex print:hidden'>
                                {
                                    loading || !data ? (
                                        <Skeleton className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group" />

                                    ) : (
                                        <button
                                            key={data.favorite} //  Forces re-render when favorite state changes
                                            type="button"
                                            onClick={(e) => handleToggleFavorite(e, data.id)}
                                            className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
                                        >
                                            <FavoritesIcon
                                                filled={Boolean(data?.favorite)}
                                                className={`w-7 h-7 transition-colors duration-200 ${data?.favorite ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                                                    }`}
                                            />
                                        </button>
                                    )
                                }
                                <a href="#" className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group">
                                    <DownloadIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
                                </a>
                                <button type="button" onClick={(e) => {
                                    e.preventDefault();
                                    handleNativePrint()
                                }} className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group">
                                    <PrintIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
                                </button>
                                <a
                                    href="#"
                                    className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
                                    onClick={(e) => {
                                        e.preventDefault(); // Prevent navigation
                                        copyToClipboard();
                                    }}
                                >
                                    <LinkIcon className={`text-gray-700 w-7 h-7 group-hover:text-gold-500 ${copied ? "text-green-500 group-hover:text-green-500" : ""}`} />
                                </a>
                            </div>

                            {/* Tabs using shadcn */}
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white-50">
                                <TabsList className="print-hidden relative flex flex-col justify-around w-full h-auto border-gray-100 rounded-none border-b-1 md:flex-row bg-white-50">
                                    <div className="flex flex-row w-full md:items-center">
                                        {loading ? (
                                            //  Skeleton for Tabs while loading
                                            <>
                                                <Skeleton className="w-16 h-8 mx-2 rounded" />
                                                <Skeleton className="w-16 h-8 mx-2 rounded" />
                                            </>
                                        ) : (
                                            tabsDataWithContent.map((tab) => (
                                                <TabsTrigger
                                                    key={tab.value}
                                                    value={tab.value}
                                                    className="print:hidden -mb-[13px] p-4 text-gray-400 text-base leading-5 font-semibold cursor-pointer font-redhat !shadow-none border-b-1 border-transparent data-[state=active]:text-gold-500 data-[state=active]:border-gold-500 rounded-none"
                                                >
                                                    {tab.label}
                                                </TabsTrigger>
                                            ))
                                        )}
                                    </div>
                                    <div className="flex w-full md:place-content-end">
                                        {/* ShortForm Label */}
                                        <div className="p-4 print:hidden">
                                            {loading ? (
                                                <Skeleton className="w-20 h-5 rounded" />
                                            ) : (
                                                <label
                                                    htmlFor="shortform"
                                                    className={`print:hidden text-sm leading-4.5 ${isChecked ? 'cursor-pointer text-gray-400 font-normal' : 'text-gray-800 font-medium'
                                                        }`}
                                                >
                                                    Short Form
                                                </label>
                                            )}
                                        </div>

                                        {/* Switch Toggle */}
                                        <div className="flex items-center justify-center" ref={printBottomRef}>
                                            {loading ? (
                                                <Skeleton className="h-[23px] w-[45px] rounded-full" />
                                            ) : (
                                                <label className="print:hidden cursor-pointer relative block w-[45px] h-[23px] bg-gold-600 rounded-58 p-1.15 overflow-visible">
                                                    <input
                                                        type="checkbox"
                                                        id="shortform"
                                                        className="invisible w-0 h-0 sr-only"
                                                        checked={isChecked}
                                                        onChange={() => setIsChecked(!isChecked)}
                                                    />
                                                    {/* Slider Circle */}
                                                    <span
                                                        className={`absolute top-[1px] left-[1px] w-[23px] h-[21px] z-10 bg-white-50 rounded-58 transition-transform duration-300 ${isChecked ? 'translate-x-[20px]' : ''
                                                            }`}
                                                    ></span>

                                                    {/* Display "S" on the opposite side of the toggle */}
                                                    <span
                                                        className={`absolute top-[50%] transform -translate-y-[50%] text-white-50 font-medium leading-4.5 text-sm ${isChecked ? 'left-[5px]' : 'right-[5px]'
                                                            }`}
                                                    >
                                                        {isChecked ? 'L' : 'S'}
                                                    </span>
                                                </label>
                                            )}
                                        </div>

                                        {/* LongForm Label */}
                                        <div className="p-4">
                                            {loading ? (
                                                <Skeleton className="w-20 h-5 rounded" />
                                            ) : (
                                                <label
                                                    htmlFor="longform"
                                                    className={`text-sm leading-4.5 ${!isChecked ? 'cursor-pointer text-gray-400 font-normal' : 'text-gray-800 font-medium'
                                                        }`}
                                                >
                                                    Long Form
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                </TabsList>

                                {tabsDataWithContent.map((tab) =>
                                    tab.value === activeTab ? (
                                        <TabsContent
                                            key={tab.value}
                                            value={tab.value}
                                            className="w-full max-w-full font-normal leading-7 text-left text-gray-900 font-redhat text-18 print:break-inside-avoid print:whitespace-normal print:leading-relaxed print:text-black"
                                        >
                                            {loading ? (
                                                <Skeleton className="w-full rounded-lg h-72" />
                                            ) : isChecked && tab.value === 'english' && data?.full_description ? (
                                                <div dangerouslySetInnerHTML={{ __html: data.full_description }} />
                                            ) : (
                                                // <div className="content entry-content">
                                                //     {tab.content}
                                                // </div>
                                                <div className="content entry-content my-audio-content">
                                                    {tab.content}
                                                    {/* Fallback TrinityAudioPlayer if native audio is not present */}
                                                    {tab.value === 'english' && (!data?.english_audio || !data.english_audio.match(/\.(mp3|ogg|wav)$/i)) && (
                                                        <TrinityAudioPlayer selector=".my-audio-content" langKey="en" />
                                                    )}
                                                    {tab.value === 'latin' && (!data?.latin_audio || !data.latin_audio.match(/\.(mp3|ogg|wav)$/i)) && (
                                                        <TrinityAudioPlayer selector=".my-audio-content" langKey="la" />
                                                    )}
                                                </div>

                                            )}
                                        </TabsContent>
                                    ) : null
                                )}

                            </Tabs>

                        </div>
                        {/* <div className='block px-4 pt-8 mb-8 border-t border-gray-100 xl:px-12 lg:mb-0 lg:hidden'>
                            <h5 className='mb-3 text-base font-semibold leading-5 text-gray-900'>Author:</h5>
                            <div className='flex flex-row items-center gap-2 mb-8'>
                                <img src="/favicon.png" alt="Favicon" className="w-7 h-7" />
                                <p className='font-medium text-sm leading-4.5 text-gray-700'>{data?.author}</p>
                            </div>
                            <h5 className='mb-3 text-base font-semibold leading-5 text-gray-900'>Source:</h5>
                            <p className='text-base font-semibold leading-5 text-gray-900'>
                                This Family activity appears in <span className='text-gold-300'>"{data?.source}"</span>
                            </p>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SinglePrayerAndRites;